/**
 * Client-side rate limiting utilities.
 *
 * Implements token bucket algorithm for request throttling to prevent
 * rapid API requests and protect against unnecessary backend load.
 *
 * @module rateLimiter
 */

/**
 * Rate limit configuration for a specific endpoint type
 */
export interface RateLimitConfig {
  /** Maximum number of requests allowed */
  readonly maxRequests: number;
  /** Time window in milliseconds */
  readonly windowMs: number;
  /** Burst capacity (allows initial burst of requests) */
  readonly burstCapacity?: number;
}

/**
 * Rate limit state for tracking tokens
 */
interface RateLimitState {
  /** Current number of available tokens */
  tokens: number;
  /** Last time tokens were refilled */
  lastRefill: number;
  /** Maximum tokens (bucket capacity) */
  maxTokens: number;
  /** Tokens refilled per window */
  refillRate: number;
  /** Window size in milliseconds */
  windowMs: number;
}

/**
 * Default rate limit configurations per HTTP method
 */
export const DEFAULT_RATE_LIMITS: Record<string, RateLimitConfig> = {
  GET: {
    maxRequests: 30,
    windowMs: 60 * 1000, // 1 minute
    burstCapacity: 10, // Allow initial burst of 10 requests
  },
  POST: {
    maxRequests: 10,
    windowMs: 60 * 1000, // 1 minute
    burstCapacity: 3, // Allow initial burst of 3 requests
  },
  PUT: {
    maxRequests: 10,
    windowMs: 60 * 1000, // 1 minute
    burstCapacity: 3,
  },
  PATCH: {
    maxRequests: 10,
    windowMs: 60 * 1000, // 1 minute
    burstCapacity: 3,
  },
  DELETE: {
    maxRequests: 5,
    windowMs: 60 * 1000, // 1 minute
    burstCapacity: 2, // Very conservative for destructive operations
  },
};

/**
 * Global rate limit state map
 * Key: HTTP method (GET, POST, etc.)
 * Value: Rate limit state for that method
 */
const rateLimitStates = new Map<string, RateLimitState>();

/**
 * Initialize or get rate limit state for a method
 */
function getOrCreateState(method: string, config: RateLimitConfig): RateLimitState {
  let state = rateLimitStates.get(method);

  if (!state) {
    const maxTokens = config.burstCapacity ?? config.maxRequests;
    state = {
      tokens: maxTokens,
      lastRefill: Date.now(),
      maxTokens,
      refillRate: config.maxRequests / (config.windowMs / 1000), // tokens per second
      windowMs: config.windowMs,
    };
    rateLimitStates.set(method, state);
  }

  return state;
}

/**
 * Refill tokens based on elapsed time
 */
function refillTokens(state: RateLimitState, now: number): void {
  const elapsed = now - state.lastRefill;
  const tokensToAdd = (elapsed / 1000) * state.refillRate; // tokens per second

  if (tokensToAdd > 0) {
    state.tokens = Math.min(state.maxTokens, state.tokens + tokensToAdd);
    state.lastRefill = now;
  }
}

/**
 * Check if a request is allowed based on rate limit
 *
 * @param method - HTTP method (GET, POST, etc.)
 * @param config - Rate limit configuration (uses default if not provided)
 * @returns Object with `allowed` boolean and `retryAfter` in milliseconds if not allowed
 */
export function checkRateLimit(
  method: string,
  config?: RateLimitConfig,
): { allowed: boolean; retryAfter?: number } {
  const effectiveConfig = config ?? DEFAULT_RATE_LIMITS[method] ?? DEFAULT_RATE_LIMITS.GET;
  const state = getOrCreateState(method, effectiveConfig);
  const now = Date.now();

  // Refill tokens based on elapsed time
  refillTokens(state, now);

  // Check if we have enough tokens
  if (state.tokens >= 1) {
    // Consume one token
    state.tokens -= 1;
    return { allowed: true };
  }

  // Calculate when next token will be available
  const tokensNeeded = 1 - state.tokens;
  const timeToWait = (tokensNeeded / state.refillRate) * 1000; // milliseconds
  const retryAfter = Math.ceil(timeToWait);

  return {
    allowed: false,
    retryAfter,
  };
}

/**
 * Get current rate limit status for a method
 *
 * @param method - HTTP method
 * @returns Current token count and max tokens, or null if not initialized
 */
export function getRateLimitStatus(method: string): { tokens: number; maxTokens: number } | null {
  const state = rateLimitStates.get(method);
  if (!state) {
    return null;
  }

  // Refill tokens before returning status
  refillTokens(state, Date.now());

  return {
    tokens: Math.max(0, Math.floor(state.tokens)),
    maxTokens: state.maxTokens,
  };
}

/**
 * Reset rate limit state for a method (useful for testing or manual reset)
 *
 * @param method - HTTP method to reset, or undefined to reset all
 */
export function resetRateLimit(method?: string): void {
  if (method) {
    rateLimitStates.delete(method);
  } else {
    rateLimitStates.clear();
  }
}

